// Scoped to the Red Flag product template only. Not referenced by any other
// template/section, so it never loads on other product pages.
//
// Responsibilities:
//   1. Variant card selection -> updates every hidden `[name="id"]` variant
//      input on the page (hero CTA form + sticky bar form) and dispatches
//      `red-flag:variant:change` so other components can react.
//   2. Sticky mobile bottom bar: show/hide on scroll, mirrors the hero CTA.
//   3. Lightweight scroll-snap carousel controls (results carousel).

(() => {
  if (window.__redFlagProductInit) return;
  window.__redFlagProductInit = true;

  const VARIANT_EVENT = 'red-flag:variant:change';

  function initVariantPickers() {
    const cards = document.querySelectorAll('[data-red-flag-variant-card]');
    cards.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const detail = {
          variantId: input.dataset.variantId,
          price: input.dataset.price,
          compareAtPrice: input.dataset.compareAtPrice || '',
          title: input.dataset.variantTitle || '',
          isBundle: input.dataset.isBundle === 'true',
        };

        document
          .querySelectorAll('[data-red-flag-variant-input]')
          .forEach((hidden) => {
            hidden.value = detail.variantId;
            hidden.dispatchEvent(new Event('change', { bubbles: true }));
          });

        document.dispatchEvent(new CustomEvent(VARIANT_EVENT, { detail }));
      });

      if (input.checked) {
        // Sync on load so the sticky bar / anything listening gets the
        // initial state without requiring a user interaction first.
        input.dispatchEvent(new Event('change'));
      }
    });
  }

  function initStickyBar() {
    const bar = document.querySelector('[data-red-flag-sticky-bar]');
    const hero = document.querySelector('[data-red-flag-hero]');
    if (!bar || !hero) return;

    const priceEl = bar.querySelector('[data-red-flag-sticky-price]');
    const ctaSlot = bar.querySelector('[data-red-flag-sticky-cta-slot]');
    const heroCta = hero.querySelector('[data-red-flag-cta]');

    // Single source of truth: clone the hero's CTA (whichever launch state
    // is active) into the sticky bar instead of duplicating state settings.
    if (ctaSlot && heroCta) {
      ctaSlot.appendChild(heroCta.cloneNode(true));
    }

    document.addEventListener(VARIANT_EVENT, (event) => {
      if (priceEl && event.detail.price) {
        priceEl.textContent = event.detail.price;
      }
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        bar.classList.toggle('rf-sticky-bar--visible', !entry.isIntersecting);
      },
      { rootMargin: '-1px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(hero);
  }

  function initQuantityUpsell() {
    document.querySelectorAll('[data-red-flag-quantity-stepper]').forEach((stepper) => {
      const valueEl = stepper.querySelector('[data-red-flag-quantity-value]');
      const decrease = stepper.querySelector('[data-red-flag-quantity-decrease]');
      const increase = stepper.querySelector('[data-red-flag-quantity-increase]');
      if (!valueEl) return;

      function setQuantity(next) {
        const clamped = Math.max(1, next);
        valueEl.textContent = String(clamped);
        document
          .querySelectorAll('[data-red-flag-quantity-input]')
          .forEach((hidden) => {
            hidden.value = String(clamped);
            hidden.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      decrease?.addEventListener('click', () => {
        setQuantity(parseInt(valueEl.textContent, 10) - 1);
      });
      increase?.addEventListener('click', () => {
        setQuantity(parseInt(valueEl.textContent, 10) + 1);
      });
    });
  }

  function initCarousels() {
    document.querySelectorAll('[data-red-flag-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-red-flag-carousel-track]');
      const prev = carousel.querySelector('[data-red-flag-carousel-prev]');
      const next = carousel.querySelector('[data-red-flag-carousel-next]');
      if (!track) return;

      function scrollByCard(direction) {
        const card = track.querySelector('[data-red-flag-carousel-card]');
        const amount = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
        track.scrollBy({ left: amount * direction, behavior: 'smooth' });
      }

      prev?.addEventListener('click', () => scrollByCard(-1));
      next?.addEventListener('click', () => scrollByCard(1));
    });
  }

  function init() {
    initVariantPickers();
    initStickyBar();
    initQuantityUpsell();
    initCarousels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
